import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  CheckCheck,
  Megaphone,
  AlertTriangle,
  Workflow,
  MessageSquare,
  Wallet,
  UserPlus,
  Headphones,
  ArrowUpRight,
  Info,
  Webhook,
  ArrowUpFromLine,
  ArrowDownToLine,
} from 'lucide-react'
import { toast } from 'sonner'

import { notificationsApi } from '@/api/endpoints/notifications'
import { queryKeys } from '@/api/query-keys'
import { useNotificationStore } from '@/stores/notificationStore'
import { NOTIFICATION_ROUTES } from '@/lib/notification-routes'
import { formatDate, cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { Notification, NotificationType } from '@/types'
import { NoDataFound } from '@/components/shared/no-data-found'

// ─── Icon mapping by notification type ───────────────────────────────

const NOTIFICATION_ICONS: Record<NotificationType, React.ElementType> = {
  campaign_completed: Megaphone,
  campaign_failed: AlertTriangle,
  flow_error: AlertTriangle,
  flow_completed: Workflow,
  new_message: MessageSquare,
  low_balance: Wallet,
  team_invite: UserPlus,
  chat_assigned: Headphones,
  chat_escalated: ArrowUpRight,
  system_update: Info,
  webhook_failure: Webhook,
  import_completed: ArrowUpFromLine,
  import_failed: AlertTriangle,
  export_completed: ArrowDownToLine,
  export_failed: AlertTriangle,
}

const NOTIFICATION_ICON_COLORS: Record<NotificationType, string> = {
  campaign_completed: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-950/50',
  campaign_failed: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
  flow_error: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
  flow_completed: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/50',
  new_message: 'text-primary bg-primary/10',
  low_balance: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/50',
  team_invite: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-950/50',
  chat_assigned: 'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-950/50',
  chat_escalated: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-950/50',
  system_update: 'text-muted-foreground bg-muted',
  webhook_failure: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
  import_completed: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-950/50',
  import_failed: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
  export_completed: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/50',
  export_failed: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
}

// ─── Single notification item ────────────────────────────────────────

function NotificationItem({
  notification,
  onNavigate,
  onMarkRead,
}: {
  notification: Notification
  onNavigate: (route: string) => void
  onMarkRead: (id: string) => void
}) {
  const Icon = NOTIFICATION_ICONS[notification.notification_type] || Info
  const iconColor = NOTIFICATION_ICON_COLORS[notification.notification_type] || 'text-muted-foreground bg-muted'
  // A notification may carry its own page (e.g. CSAT low rating → Follow-ups).
  const own = notification.data?.route
  const route = typeof own === 'string' && own.startsWith('/') ? own : NOTIFICATION_ROUTES[notification.notification_type]

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkRead(notification.id)
    }
    if (route) {
      onNavigate(route)
    }
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
        !notification.is_read && 'bg-primary/5',
      )}
    >
      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', iconColor)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={cn('text-sm truncate', !notification.is_read ? 'heading-text font-bold' : 'heading-text font-semibold')}>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
          {notification.body}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/70">
          {formatDate(notification.created_at, 'relative')}
        </p>
      </div>
    </button>
  )
}

// ─── Loading skeleton ────────────────────────────────────────────────

function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}

// ─── Main NotificationPanel ──────────────────────────────────────────

export function NotificationPanel() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { unreadCount, setUnreadCount, isOpen, openPanel, closePanel } = useNotificationStore()

  // Fetch initial unread count
  const { data: serverUnreadCount } = useQuery({
    queryKey: queryKeys.notificationUnreadCount,
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 60000, // Poll every minute as fallback
  })

  // Sync server count to Zustand store via useEffect (not during render)
  useEffect(() => {
    if (serverUnreadCount !== undefined) {
      setUnreadCount(serverUnreadCount)
    }
  }, [serverUnreadCount, setUnreadCount])

  // Fetch recent notifications when panel is open
  const {
    data: notificationsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.notifications({ page_size: 20 }),
    queryFn: () => notificationsApi.getNotifications({ page_size: 20 }),
    enabled: isOpen,
  })

  const notifications = notificationsData?.results ?? []

  // Mark read mutation
  const markReadMutation = useMutation({
    mutationFn: (ids: string[]) => notificationsApi.markRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  // Mark all read mutation
  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      setUnreadCount(0)
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: () => toast.error('Failed to mark notifications as read'),
  })

  const handleNavigate = (route: string) => {
    closePanel()
    navigate(route)
  }

  const handleMarkRead = (id: string) => {
    markReadMutation.mutate([id])
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => (open ? openPanel() : closePanel())}>
      <PopoverTrigger asChild>
        <button type="button" className="header-action-item relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute right-0 top-0 flex h-4 min-w-4 -translate-y-1/4 translate-x-1/4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] overflow-hidden p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-clay-200 px-4 py-3 dark:border-clay-700">
          <h5>Notifications</h5>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>

        <Separator />

        {/* Notification list — plain overflow scroll: Radix ScrollArea
            doesn't constrain its viewport from a max-h on the root, so the
            list rendered full-height and never scrolled. */}
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading && (
            <div className="divide-y">
              {Array.from({ length: 4 }).map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          )}

          {isError && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">Failed to load notifications</p>
            </div>
          )}

          {!isLoading && !isError && notifications.length === 0 && (
            <NoDataFound size="sm" description="No notifications yet" />
          )}

          {!isLoading && !isError && notifications.length > 0 && (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onNavigate={handleNavigate}
                  onMarkRead={handleMarkRead}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={() => handleNavigate('/notifications')}
              >
                View all notifications
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
